import { Check, Loader2, Pencil, X } from 'lucide-react'
import type { ComponentType } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'

export interface ContactFieldProps {
  label: string
  icon: ComponentType<{ className?: string }>
  value: string | undefined
  linkedValues: string[]
  onSave: (next: string) => Promise<void>
}

// Tile for Email/Phone that combines the inline-editable Person property with
// linked Email/Phone nodes (HAS_EMAIL / HAS_PHONE). The first available value
// is shown in the primary slot; the rest render as read-only chips below.
export function ContactField({
  label,
  icon: Icon,
  value,
  linkedValues,
  onSave,
}: ContactFieldProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = () => {
    setDraft(value ?? '')
    setError(null)
    setEditing(true)
  }
  const cancel = () => {
    setEditing(false)
    setError(null)
  }
  const commit = async () => {
    const next = draft.trim()
    if (next === (value ?? '').trim()) {
      cancel()
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(next)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('identity.detail.errors.save'))
    } finally {
      setSaving(false)
    }
  }

  const hasDirect = !!value
  const totalCount = (hasDirect ? 1 : 0) + linkedValues.length
  const primaryDisplay = hasDirect ? value : linkedValues[0]
  const chipValues = hasDirect ? linkedValues : linkedValues.slice(1)

  if (editing) {
    return (
      <div className="flex flex-col gap-2 p-3 rounded-lg bg-muted border border-primary/40 ring-1 ring-primary/20">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <div className="flex items-center gap-1 mt-1">
              <Input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void commit()
                  if (e.key === 'Escape') cancel()
                }}
                disabled={saving}
                className="h-9 md:h-7"
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 md:h-7 md:w-7"
                onClick={() => void commit()}
                disabled={saving}
                aria-label={t('identity.detail.save')}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5 text-success" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 md:h-7 md:w-7"
                onClick={cancel}
                disabled={saving}
                aria-label={t('identity.detail.cancel')}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {error && <p className="text-xs text-destructive mt-1">{error}</p>}
          </div>
        </div>
        {linkedValues.length > 0 && (
          <div className="ml-8 flex flex-wrap gap-1">
            {linkedValues.map((v, i) => (
              <Badge
                key={`${v}-${i}`}
                variant="outline"
                className="text-xs font-mono border-primary/30 text-muted-foreground"
              >
                {v}
              </Badge>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col rounded-lg bg-muted border border-transparent hover:border-primary/40 transition-colors">
      <button
        type="button"
        onClick={start}
        aria-label={t('identity.detail.edit', { label })}
        className="group flex items-center gap-3 p-3 text-left w-full cursor-pointer hover:bg-muted/80 transition-colors rounded-lg"
      >
        <Icon className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-xs text-muted-foreground">{label}</p>
            {totalCount > 1 && (
              <Badge
                variant="outline"
                className="h-4 px-1.5 text-[10px] border-primary/40 text-primary tabular-nums"
              >
                {totalCount}
              </Badge>
            )}
          </div>
          <p
            className={cn(
              'truncate font-medium',
              primaryDisplay ? 'text-foreground' : 'text-muted-foreground italic font-normal'
            )}
          >
            {primaryDisplay || t('identity.detail.clickToAdd')}
          </p>
        </div>
        <Pencil className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-primary transition-colors shrink-0" />
      </button>
      {chipValues.length > 0 && (
        <div className="px-3 pb-3 ml-8 flex flex-wrap gap-1">
          {chipValues.map((v, i) => (
            <Badge
              key={`${v}-${i}`}
              variant="outline"
              className="text-xs font-mono border-primary/30 text-muted-foreground"
            >
              {v}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
